'use client';

import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import styles from './Table.module.css';
import clsx from 'clsx';

export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  className?: string;
}

export interface TableHeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  className?: string;
}

export interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  className?: string;
}

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
  className?: string;
}

export interface TableHeaderProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: () => void;
  responsive?: boolean;
  className?: string;
}

export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  responsive?: boolean;
  className?: string;
}

export interface TableActionItem {
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

export interface TableActionCellProps {
  items: TableActionItem[];
}

export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ striped, hoverable, compact, className, children, ...props }, ref) => {
    return (
      <div className={styles.wrapper}>
        <table
          ref={ref}
          className={clsx(
            styles.table,
            striped && styles.striped,
            hoverable && styles.hoverable,
            compact && styles.compact,
            className
          )}
          {...props}
        >
          {children}
        </table>
      </div>
    );
  }
);

Table.displayName = 'Table';

export const TableHead = React.forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <thead ref={ref} className={clsx(styles.head, className)} {...props}>
        {children}
      </thead>
    );
  }
);

TableHead.displayName = 'TableHead';

export const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <tbody ref={ref} className={clsx(styles.body, className)} {...props}>
        {children}
      </tbody>
    );
  }
);

TableBody.displayName = 'TableBody';

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ selected, className, children, ...props }, ref) => {
    return (
      <tr
        ref={ref}
        className={clsx(styles.row, selected && styles.selected, className)}
        {...props}
      >
        {children}
      </tr>
    );
  }
);

TableRow.displayName = 'TableRow';

export const TableHeader = React.forwardRef<HTMLTableCellElement, TableHeaderProps>(
  ({ sortable, sortDirection, onSort, responsive, className, children, ...props }, ref) => {
    return (
      <th
        ref={ref}
        className={clsx(
          styles.header,
          sortable && styles.sortable,
          responsive && styles.responsive,
          className
        )}
        onClick={sortable ? onSort : undefined}
        aria-sort={sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : undefined}
        {...props}
      >
        <span className={styles.headerContent}>
          {children}
          {sortable && (
            <span className={styles.sortIcon}>
              {sortDirection === 'asc' && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 15l-6-6-6 6" />
                </svg>
              )}
              {sortDirection === 'desc' && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              )}
              {!sortDirection && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.3">
                  <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
                </svg>
              )}
            </span>
          )}
        </span>
      </th>
    );
  }
);

TableHeader.displayName = 'TableHeader';

export const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ responsive, className, children, ...props }, ref) => {
    return (
      <td ref={ref} className={clsx(styles.cell, responsive && styles.responsive, className)} {...props}>
        {children}
      </td>
    );
  }
);

TableCell.displayName = 'TableCell';

export interface TableLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   * Element/component to render as (e.g. Next.js Link) for client-side routing.
   * Defaults to a plain anchor tag.
   */
  component?: React.ElementType;
  className?: string;
}

/**
 * Standard link style for content inside table cells (e.g. a row's name column).
 * Always use this — rather than an unstyled MUI/Next Link — so every table's
 * links share the same weight, size, and accent color.
 */
export const TableLink = React.forwardRef<HTMLAnchorElement, TableLinkProps>(
  ({ component: Component = 'a', className, children, ...props }, ref) => {
    return (
      <Component ref={ref} className={clsx(styles.link, className)} {...props}>
        {children}
      </Component>
    );
  }
);

TableLink.displayName = 'TableLink';

function KebabIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}

export function TableActionCell({ items }: TableActionCellProps) {
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const isMenuOpen = menuPos !== null;

  useEffect(() => {
    if (!isMenuOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setMenuPos(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  // Keep the menu anchored to its trigger button as the page (or any
  // scrollable ancestor, e.g. the table's horizontal scroll wrapper) scrolls,
  // instead of staying fixed in the viewport while the row scrolls away.
  useEffect(() => {
    if (!isMenuOpen) return;
    // Scroll fires far faster than the screen repaints, and it's registered
    // in the capture phase (so every scrollable ancestor triggers it), so
    // coalesce a burst of events into one measurement per frame.
    let frame = 0;
    function updatePosition() {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        const top = rect.bottom + 4;
        const right = window.innerWidth - rect.right;
        // Returning the previous object lets React bail out of the re-render
        // entirely when the menu hasn't actually moved.
        setMenuPos((prev) =>
          prev && prev.top === top && prev.right === right ? prev : { top, right }
        );
      });
    }
    window.addEventListener('scroll', updatePosition, { passive: true, capture: true });
    window.addEventListener('resize', updatePosition);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isMenuOpen]);

  function handleOpen() {
    if (menuPos) { setMenuPos(null); return; }
    const rect = btnRef.current!.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }

  return (
    <td className={clsx(styles.cell, styles.actionCell)}>
      <button
        ref={btnRef}
        className={styles.kebabBtn}
        onClick={handleOpen}
        aria-label="Row actions"
      >
        <KebabIcon />
      </button>
      {menuPos && (
        <div
          ref={menuRef}
          className={styles.actionMenu}
          style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
        >
          {items.map((item, idx) => (
            <button
              key={idx}
              className={clsx(styles.actionMenuItem, item.destructive && styles.actionMenuItemDestructive)}
              onClick={() => { item.onClick(); setMenuPos(null); }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </td>
  );
}
